import { ChangeEventWorkflow, SaveChangeEventInput } from "src/generated/graphqlTypes";
import { useDeleteChangeEventMutation } from "src/routes/projects/change-events/ChangeEventPage.generated";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import {
  WorkflowActionArgs,
  WorkflowActions,
  WorkflowPageActionArgs,
} from "src/routes/projects/change-events/guided/workflows/types";
import { useGuidedChangeEventActions } from "../sharedSteps/useGuidedChangeEventActions";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";

/**
 * Saves the apply configuration change event.
 *
 * A config change's `target` can't be updated on BE (its composed `result` would describe the old config), so
 * picking a different configuration on an already-saved draft deletes that draft and creates a new one.
 */
export function useSaveApplyConfigurationChangeEvent(args: WorkflowActionArgs<ApplyConfigurationFormInput>) {
  const { form } = args;
  const [deleteChangeEvent] = useDeleteChangeEventMutation();
  const { saveChangeEvent: saveGuidedChangeEvent } = useGuidedChangeEventActions(args, applyConfigurationSaveInput);

  async function saveChangeEvent(opts?: { andExit?: boolean; dryRun?: boolean }): Promise<void> {
    // Delete first, then create
    if (form.id.value && form.targetConfigId.dirty && form.canSave()) {
      await deleteChangeEvent({ variables: { input: { id: form.id.value } } });
      // `targetConfigId` stays dirty, so the save below still goes through, as a create
      form.set({ id: undefined, configChangeId: undefined });
    }
    return saveGuidedChangeEvent(opts);
  }

  return { saveChangeEvent };
}

/** The config change intent an apply-configuration change event is saved with. */
export function applyConfigurationSaveInput(
  form: WorkflowActionArgs<ApplyConfigurationFormInput>["form"],
): Partial<SaveChangeEventInput> {
  return {
    configChange: {
      id: form.configChangeId.changedValue ?? undefined,
      targetId: form.targetConfigId.value,
      ...(sendsOptionIds(form) ? { optionIds: form.optionIds.value } : {}),
    },
  };
}

/**
 * Whether the save carries the resulting option set.
 *
 * - Initial applies its target whole, so it never composes options of its own.
 * - Partial leaves them out on create (the first save, or the re-create after a target change), so the backend seeds
 *   `result` with the default selection (every add/change, no removes) for Select Changes. Afterwards `optionIds` is
 *   what the user picked on Select Changes -- loaded from `result` and only ever moved by that step -- so Select
 *   Changes' Continue and Save & Exit keep it.
 */
function sendsOptionIds(form: WorkflowActionArgs<ApplyConfigurationFormInput>["form"]): boolean {
  return form.workflow.value !== ChangeEventWorkflow.ApplyInitialConfiguration && !!form.configChangeId.value;
}

/** The actions the initial and partial apply-configuration workflows share. */
export function useApplyConfigurationActions(
  args: WorkflowPageActionArgs<ApplyConfigurationFormInput>,
): WorkflowActions {
  const { form, project } = args;
  const { saveChangeEvent } = useSaveApplyConfigurationChangeEvent(args);
  return {
    // Each workflow overrides this with the commitment cleanup gate.
    onSubmit: async () => {},
    onContinue: {
      [GuidedChangeEventStep.SelectConfiguration]: async () => {
        // With no signed contract the initial configuration is applied directly later, so there is no draft to
        // create; the save is only a dry run, so the Review Impact / Review steps preview the real derivation from the
        // cache. Partial is only offered on projects with a signed contract (`Project.canApplyConfigChange`),
        // so it always saves.
        const isInitial = form.workflow.value === ChangeEventWorkflow.ApplyInitialConfiguration;
        await saveChangeEvent({ dryRun: isInitial && !project.latestActiveStage?.hasSignedContract });
      },
      // Persists the picked changes as the config change's `result`, which the backend re-resolves (`fixStaleConfig`)
      // and the save folds back into `optionIds`
      [GuidedChangeEventStep.SelectChanges]: () => saveChangeEvent(),
    },
    onSaveAndExit: form.value.id ? () => saveChangeEvent({ andExit: true }) : undefined,
  };
}
